import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Download } from "lucide-react";
import { useLang } from "../LanguageContext";
import Seo from "../components/Seo";
import { SITE_URL } from "../seoConfig";
import { Reveal, Overline } from "../components/Reveal";
import Footer from "../components/Footer";
import Loader from "../components/Loader";

const Paragraphs = ({ text }) =>
  String(text || "")
    .split("\n\n")
    .filter(Boolean)
    .map((para, i) => (
      <p key={i} className="text-ink/70 text-lg font-light leading-relaxed mb-5 last:mb-0">{para}</p>
    ));

const PolicyPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const { t, lang, setLang, loading } = useLang();

  useEffect(() => { window.scrollTo(0, 0); }, [type]);

  if (loading) return <Loader />;

  const p = t.policies?.[type];
  if (!p) {
    return (
      <div className="min-h-screen bg-paper flex flex-col items-center justify-center gap-4">
        <p className="font-serif text-2xl text-ink">404</p>
        <Link to="/" className="text-gold hover:underline">← {lang === "sv" ? "Till startsidan" : "Back home"}</Link>
      </div>
    );
  }

  const sections = p.sections || [];
  const HERO_BG = { quality: "/policy-quality-bg.jpg", environment: "/policy-environment-bg.jpg" };
  const heroBg = p.heroImage || HERO_BG[type];

  return (
    <div className="bg-paper min-h-screen" data-testid={`policy-page-${type}`}>
      <Seo title={`${p.label} | Horns Industriservice`} description={p.intro}
        path={`/policy/${type}`} lang={lang}
        image={p.badgeImage ? `${SITE_URL}${p.badgeImage}` : undefined} />
      <header className="sticky top-0 z-40 bg-paper/90 backdrop-blur-md border-b border-border">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-[76px] flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center" data-testid="policy-logo-home">
            <img src="/horns-logo.webp" alt="Horns Industriservice" className="h-12 md:h-14 w-auto" />
          </button>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate("/")}
              className="hidden sm:flex items-center gap-2 text-ink/70 hover:text-ink text-sm tracking-[0.12em] uppercase transition-colors">
              <ArrowLeft size={16} /> {lang === "sv" ? "Startsidan" : "Home"}
            </button>
            <div className="flex items-center text-[13px] tracking-[0.15em] font-light text-ink/60">
              <button onClick={() => setLang("sv")} className={"px-1 " + (lang === "sv" ? "text-ink font-medium" : "hover:text-gold")}>SV</button>
              <span className="opacity-40">/</span>
              <button onClick={() => setLang("en")} className={"px-1 " + (lang === "en" ? "text-ink font-medium" : "hover:text-gold")}>EN</button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-ink text-white overflow-hidden grain">
        {heroBg ? (
          <div className="absolute inset-0">
            <img src={heroBg} alt="" className="h-full w-full object-cover opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/55 to-transparent" />
            <div className="absolute inset-0 bg-ink/15" />
          </div>
        ) : null}
        <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 md:pb-20 grid md:grid-cols-[1fr_auto] gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}>
            <Overline dark>{p.overline}</Overline>
            <h1 className="font-serif font-light leading-[1.04] text-4xl md:text-6xl mt-5 max-w-3xl">{p.heading}</h1>
            <p className="mt-7 text-white/70 text-lg font-light leading-relaxed max-w-2xl">{p.intro}</p>
          </motion.div>
          {p.badgeImage ? (
            <motion.img initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 0.7 }}
              src={p.badgeImage} alt={p.badge} data-testid="policy-badge-image"
              className="h-28 w-28 md:h-40 md:w-40 object-contain shrink-0 justify-self-start md:justify-self-end" />
          ) : null}
        </div>
      </section>

      {/* Sections */}
      <section className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-24">
        {sections.map((s, i) => {
          const points = s.points || [];
          return (
            <Reveal key={i} y={40}>
              <div className={i > 0 ? "mt-14 md:mt-20" : ""}>
                {s.title ? (
                  <h2 className="font-serif text-ink font-light text-3xl md:text-4xl leading-tight mb-6">{s.title}</h2>
                ) : null}
                {s.body ? <div className="max-w-2xl"><Paragraphs text={s.body} /></div> : null}
                {points.length > 0 ? (
                  <ul className="mt-2 space-y-4">
                    {points.map((point, j) => (
                      <li key={j} className="flex items-start gap-4 text-ink border-b border-border pb-4">
                        <span className="h-7 w-7 flex items-center justify-center border border-gold text-gold rounded-full shrink-0 mt-0.5">
                          <Check size={14} strokeWidth={1.5} />
                        </span>
                        <span className="font-light text-lg leading-snug">{point}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </Reveal>
          );
        })}

        {/* Certificate / PDF */}
        {(p.pdfUrl || p.badgeImage) ? (
          <Reveal y={40}>
            <div className="mt-16 md:mt-20 border-t border-border pt-10 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
              <div className="flex items-center gap-5">
                {p.badgeImage ? <img src={p.badgeImage} alt={p.badge} className="h-16 w-16 object-contain" /> : null}
                <div>
                  <p className="font-serif text-xl text-ink leading-none">{p.badge}</p>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-ink/50 mt-1.5">{p.badgeSub}</p>
                </div>
              </div>
              {p.pdfUrl ? (
                <a href={p.pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="policy-pdf-download"
                  className="group inline-flex items-center gap-3 bg-ink hover:bg-ink-soft text-white px-8 py-4 text-[12px] uppercase tracking-[0.2em] transition-colors w-fit">
                  <Download size={16} strokeWidth={1.5} />
                  {p.pdfLabel || (lang === "sv" ? "Ladda ner (PDF)" : "Download (PDF)")}
                </a>
              ) : null}
            </div>
          </Reveal>
        ) : null}
      </section>

      <Footer />
    </div>
  );
};

export default PolicyPage;
